import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useLazyRef from 'utils/useLazyRef'
import {
  MESSAGES_SET_SELECTED_ROW_IDS,
  MessagesTableDispatch,
} from 'messages/hooks/messagesServerSideSelectionConstants'
import { MessagesServerSideSelectionPluginApi } from 'messages/hooks/useMessagesServerSideSelectionPlugin'

type UseServerSideMessagesRowSelectionParams<TRow> = {
  rows: TRow[]
  getRowId: (row: TRow) => string
  totalCount: number
  listLoading: boolean
  fetchAllMatching: () => Promise<TRow[]>
}

export const useServerSideMessagesRowSelection = <TRow>({
  rows,
  getRowId,
  totalCount,
  listLoading,
  fetchAllMatching,
}: UseServerSideMessagesRowSelectionParams<TRow>) => {
  const [selectedRowIds, setSelectedRowIds] = useState<Record<string, boolean>>({})
  const [selectingAll, setSelectingAll] = useState(false)
  const selectAllInFlightRef = useRef(false)
  const rowByIdRef = useLazyRef(() => new Map<string, TRow>())
  const rowById = rowByIdRef.current as unknown as Map<string, TRow>

  const selectionPluginApiRef = useRef<MessagesServerSideSelectionPluginApi>({
    onSelectedRowIdsChange: () => undefined,
    syncRowsFromSelection: () => undefined,
  })

  useEffect(() => {
    rows.forEach(row => rowById.set(getRowId(row), row))
  }, [rows, getRowId, rowById])

  const syncRowsFromSelection = useCallback(
    (ids: Record<string, boolean>) => {
      rows.forEach(row => {
        const id = getRowId(row)
        if (ids[id]) {
          rowById.set(id, row)
        }
      })
    },
    [rows, getRowId, rowById],
  )

  selectionPluginApiRef.current = {
    onSelectedRowIdsChange: setSelectedRowIds,
    syncRowsFromSelection,
  }

  const selectedCount = useMemo(
    () => Object.keys(selectedRowIds).filter(id => selectedRowIds[id]).length,
    [selectedRowIds],
  )

  const clearSelection = useCallback((dispatch: MessagesTableDispatch) => {
    setSelectedRowIds({})
    dispatch({ type: MESSAGES_SET_SELECTED_ROW_IDS, selectedRowIds: {} })
  }, [])

  const selectAllMatching = useCallback(
    async (dispatch: MessagesTableDispatch) => {
      if (totalCount === 0 || listLoading || selectAllInFlightRef.current) {
        return
      }

      selectAllInFlightRef.current = true
      setSelectingAll(true)
      try {
        const all = await fetchAllMatching()
        all.forEach(row => rowById.set(getRowId(row), row))
        const ids = all.reduce<Record<string, boolean>>((acc, row) => {
          acc[getRowId(row)] = true
          return acc
        }, {})
        setSelectedRowIds(ids)
        dispatch({ type: MESSAGES_SET_SELECTED_ROW_IDS, selectedRowIds: ids })
      } finally {
        selectAllInFlightRef.current = false
        setSelectingAll(false)
      }
    },
    [fetchAllMatching, getRowId, listLoading, rowById, totalCount],
  )

  const getSelectedRows = useCallback((): TRow[] => {
    return Object.keys(selectedRowIds)
      .filter(id => selectedRowIds[id])
      .map(id => rowById.get(id))
      .filter((row): row is TRow => Boolean(row))
  }, [getRowId, rowById, selectedRowIds])

  return {
    selectedRowIds,
    selectedCount,
    selectingAll,
    selectionPluginApiRef,
    clearSelection,
    selectAllMatching,
    getSelectedRows,
  }
}
